import type { ButtonProps } from '@hover/blueprint';
import {
  AspectRatio,
  Body,
  HStack,
  Heading,
  Image,
  VStack,
  useBreakpointValue,
} from '@hover/blueprint';
import React from 'react';
import { Trans } from 'react-i18next';

import type { BasicCardProps } from 'src/modules/homeownerApp/components/Card';
import { BasicCard } from 'src/modules/homeownerApp/components/Card';
import { formatSpaceType } from 'src/modules/homeownerApp/components/Utils';
import { Button } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

const BasicCardButton = (props: BasicCardProps & ButtonProps) => (
  <BasicCard as={Button} {...props} />
);

export enum SpaceTypeSelectionDescription {
  EXTERIOR = 'projects.projectCreation.exteriorDescription',
  INTERIOR = 'projects.projectCreation.interiorDescription',
}

type SpaceTypeSelectionProps = {
  images: string[];
  spaceType: string;
  translationKey: SpaceTypeSelectionDescription;
  onClick: () => void;
  disabled?: boolean;
};

export const SpaceTypeSelection = ({
  translationKey,
  disabled,
  images,
  spaceType,
  onClick,
}: SpaceTypeSelectionProps) => {
  const ratio = useBreakpointValue({
    base: 3 / 4,
    tablet: 1,
    desktop: 3 / 4,
  });
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  return (
    <BasicCardButton
      data-testid="space-type-selection"
      disabled={disabled}
      hasShadow
      height="1"
      label={spaceType}
      mt="0 !important"
      onClick={onClick}
      px={{ base: 600, tablet: 1100 }}
      py={{ base: 600, tablet: 800 }}
      sx={{
        '.button-label': {
          width: '100%',
          textWrap: 'wrap',
        },
      }}
      variant="ghost"
    >
      <HStack justifyContent="center" mb={500}>
        {images.map(image => (
          <AspectRatio
            borderRadius={400}
            key={image}
            overflow="hidden"
            ratio={ratio}
            width={{ base: '82px', tablet: '128px', desktop: '112px' }}
          >
            <Image
              alt={`${spaceType}-image`}
              height="100%"
              objectPosition="top"
              src={image}
              width="100%"
            />
          </AspectRatio>
        ))}
      </HStack>
      <VStack gap={{ base: 100, tablet: 300 }} textAlign="center">
        <Heading
          color="interactive.primary"
          mb={0}
          textStyle={{ base: 'headingXs', tablet: 'headingMedium' }}
        >
          {formatSpaceType(spaceType)}
        </Heading>
        <Body
          color="text.secondary"
          mt="0 !important"
          textStyle={{ base: 'bodySmall', tablet: 'bodyMedium' }}
          w={1}
          whiteSpace="balance"
        >
          <Trans i18nKey={translationKey} t={t} />
        </Body>
      </VStack>
    </BasicCardButton>
  );
};
